<template>
  <div>
    <div class="mb-5">
      <p class="text-2xl mb-1">Backfill Audience / adn_post</p>
      <p class="text-body-2 text--secondary mb-0">
        Construire, approuver et exécuter un plan historique contrôlé.
      </p>
    </div>

    <v-alert type="warning" outlined>
      L’exécution écrit dans <code>adn_post</code> et peut appeler GA, les
      sources de statistiques IA et Qdrant. Vérifiez le périmètre du plan avant
      de l’approuver puis confirmez explicitement les étapes à exécuter.
    </v-alert>

    <v-stepper v-model="currentStep" alt-labels>
      <v-stepper-header>
        <v-stepper-step :complete="planReady" step="1"
          >Périmètre</v-stepper-step
        >
        <v-divider></v-divider>
        <v-stepper-step
          :complete="approvedPlanId !== null"
          :editable="planReady"
          step="2"
          >Vérification</v-stepper-step
        >
        <v-divider></v-divider>
        <v-stepper-step
          :complete="executionLaunched"
          :editable="approvedPlanId !== null"
          step="3"
          >Exécution</v-stepper-step
        >
      </v-stepper-header>
      <v-stepper-items>
        <v-stepper-content step="1">
          <adn-audience-backfill-plan
            scope-only
            :site-list="siteList"
            @plan-created="handlePlanCreated"
            @plan-ready="handlePlanReady"
          ></adn-audience-backfill-plan>
        </v-stepper-content>
        <v-stepper-content step="2">
          <adn-audience-backfill-plan
            review-only
            :show-recent-plans="false"
            :initial-plan-id="selectedPlanId"
            @plan-approved="handlePlanApproved"
          ></adn-audience-backfill-plan>
          <v-btn text class="mt-4" @click="currentStep = 1"
            >Retour au périmètre</v-btn
          >
        </v-stepper-content>
        <v-stepper-content step="3">
          <div class="mt-8 mb-2">
            <p class="text-xl mb-1">Exécution du plan approuvé</p>
            <p class="text-body-2 text--secondary mb-0">
              Le DAG exécute uniquement les lots figés dans le plan sélectionné.
            </p>
          </div>

          <adn-audience-backfill-execute
            :key="executeComponentKey"
            :initial-plan-id="approvedPlanId"
            @onUpdate="updateExecution"
          ></adn-audience-backfill-execute>

          <v-card class="mt-4">
            <v-card-title class="text-h6 font-weight-regular">
              Confirmation et lancement
            </v-card-title>
            <v-card-text>
              <v-alert v-if="executionErrors.length" type="info" outlined dense>
                <ul>
                  <li v-for="error in executionErrors" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </v-alert>

              <ul v-if="executionTexts.length" class="mb-4">
                <li v-for="text in executionTexts" :key="text">
                  {{ text }}
                </li>
              </ul>

              <v-alert
                v-if="executionLaunched"
                :type="executionAlertType"
                outlined
                dense
              >
                Plan #{{ launchedPlanId }} — statut persistant :
                <b>{{ executionPlanStatus }}</b>.
                <span v-if="executionPlanStatus === 'approved'">
                  Le workflow a été déclenché et attend sa prise en charge par
                  Airflow.
                </span>
                <span v-else-if="executionPlanStatus === 'running'">
                  Le backfill est en cours.
                </span>
                <span v-else-if="executionPlanStatus === 'completed'">
                  Toutes les étapes sélectionnées ont réussi.
                </span>
                <span v-else-if="executionPlanStatus === 'failed'">
                  Au moins une étape a échoué. Le détail ci-dessous provient de
                  la base RMR, qui reste la source de vérité même si un statut
                  Airflow est modifié manuellement.
                </span>
                <ul v-if="executionStageStatuses.length" class="mt-2 mb-0">
                  <li
                    v-for="stage in executionStageStatuses"
                    :key="stage.name"
                  >
                    {{ stage.name }} : {{ stage.succeeded ? "OK" : "échec" }}
                  </li>
                </ul>
              </v-alert>

              <v-btn text class="mr-2" @click="currentStep = 2">
                Retour à la vérification
              </v-btn>
              <v-btn
                color="primary"
                :loading="launching"
                :disabled="
                  executionErrors.length > 0 || launching || executionLaunched
                "
                @click="runBackfill"
              >
                Lancer le backfill approuvé
              </v-btn>
            </v-card-text>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </div>
</template>

<script>
import axios from "@axios";
import store from "@/store";
import AdnAudienceBackfillExecute from "@/views/pages/admin/includes/airflow/AdnAudienceBackfillExecute";
import AdnAudienceBackfillPlan from "@/views/pages/admin/includes/airflow/AdnAudienceBackfillPlan";

export default {
  name: "AdnAudienceBackfill",
  components: {
    AdnAudienceBackfillExecute,
    AdnAudienceBackfillPlan,
  },
  data() {
    return {
      siteList: [],
      currentStep: 1,
      selectedPlanId: null,
      planReady: false,
      approvedPlanId: null,
      executeComponentKey: 0,
      executionParams: {},
      executionErrors: ["Sélectionnez et confirmez un plan approuvé."],
      executionTexts: [],
      launching: false,
      executionLaunched: false,
      launchedPlanId: null,
      executionPlan: null,
      executionPollTimer: null,
    };
  },
  computed: {
    executionPlanStatus() {
      return this.executionPlan?.status || "déclenché";
    },
    executionAlertType() {
      if (this.executionPlanStatus === "completed") return "success";
      if (this.executionPlanStatus === "failed") return "error";
      return "info";
    },
    executionStageStatuses() {
      const statuses = this.executionPlan?.result_json?.stage_status || {};
      return Object.entries(statuses).map(([name, succeeded]) => ({
        name,
        succeeded,
      }));
    },
  },
  async created() {
    this.siteList = (await this.$store.dispatch("common/getSiteList")) || [];
  },
  beforeDestroy() {
    this.stopExecutionPolling();
  },
  methods: {
    handlePlanCreated(plan) {
      this.selectedPlanId = plan.id;
      this.planReady = false;
      this.approvedPlanId = null;
      this.executionLaunched = false;
      this.launchedPlanId = null;
      this.executionPlan = null;
      this.stopExecutionPolling();
    },
    handlePlanReady(plan) {
      this.selectedPlanId = plan.id;
      this.planReady = true;
      this.currentStep = 2;
    },
    handlePlanApproved(plan) {
      this.approvedPlanId = plan.id;
      this.selectedPlanId = plan.id;
      this.planReady = true;
      this.executeComponentKey += 1;
      this.executionLaunched = false;
      this.launchedPlanId = null;
      this.executionPlan = null;
      this.stopExecutionPolling();
      this.currentStep = 3;
    },
    updateExecution(configuration) {
      if (
        this.executionLaunched &&
        configuration.params.plan_id !== this.launchedPlanId
      ) {
        this.executionLaunched = false;
        this.launchedPlanId = null;
        this.executionPlan = null;
        this.stopExecutionPolling();
      }
      this.executionParams = configuration.params;
      this.executionErrors = configuration.errors;
      this.executionTexts = configuration.texts;
    },
    async runBackfill() {
      if (this.executionErrors.length || this.launching) {
        return;
      }
      this.launching = true;
      try {
        await axios.post("/airflow/dag/run", {
          dag_id: "adn_audience_backfill_execute_dag",
          dag_params: this.executionParams,
        });
        this.executionLaunched = true;
        this.launchedPlanId = this.executionParams.plan_id;
        this.startExecutionPolling();
        try {
          await this.refreshExecutionPlan();
        } catch {
          // The DAG was launched successfully. Polling will retry the status API.
        }
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Workflow de backfill Audience lancé avec succès.",
          color: "success",
          timeout: 5000,
        });
      } catch (error) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text:
            "Erreur(s): " +
            JSON.stringify(error.response?.data || error.message, null, 2),
          color: "warning",
          timeout: 5000,
        });
      } finally {
        this.launching = false;
      }
    },
    async refreshExecutionPlan() {
      if (!this.launchedPlanId) return;
      const response = await axios.get(
        `/adn-audience-backfill/plans/${this.launchedPlanId}`
      );
      this.executionPlan = response.data;
      if (["completed", "failed", "invalid"].includes(response.data.status)) {
        this.stopExecutionPolling();
      }
    },
    startExecutionPolling() {
      this.stopExecutionPolling();
      this.executionPollTimer = setInterval(async () => {
        try {
          await this.refreshExecutionPlan();
        } catch {
          // Keep polling: a temporary API failure must not hide the execution.
        }
      }, 5000);
    },
    stopExecutionPolling() {
      if (this.executionPollTimer) {
        clearInterval(this.executionPollTimer);
        this.executionPollTimer = null;
      }
    },
  },
};
</script>
